import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Truck, MapPin, Sun, Moon, Utensils, Wine, Beer, ChefHat, 
  Calendar, Clock, Euro, AlertCircle, Loader2, Map, GlassWater, X, ZoomIn, ZoomOut
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// HEC Campus Map Image
const MAP_IMAGE_URL = "https://customer-assets.emergentagent.com/job_fleet-manager-378/artifacts/x5mi3o3f_WhatsApp%20Image%202026-03-12%20at%2013.29.47.jpeg";

// Zone colors configuration
const ZONE_COLORS = {
  "Volley": { bg: "bg-yellow-100", border: "border-yellow-400", text: "text-yellow-800", dot: "bg-yellow-500", hex: "#FEF08A", borderHex: "#FACC15" },
  "Central": { bg: "bg-emerald-100", border: "border-emerald-400", text: "text-emerald-800", dot: "bg-emerald-500", hex: "#A7F3D0", borderHex: "#10B981" },
  "Foot": { bg: "bg-blue-100", border: "border-blue-400", text: "text-blue-800", dot: "bg-blue-500", hex: "#BFDBFE", borderHex: "#3B82F6" },
  "Hall d'honneur": { bg: "bg-orange-100", border: "border-orange-400", text: "text-orange-800", dot: "bg-orange-500", hex: "#FED7AA", borderHex: "#F97316" },
};

// Default zone positions on the HEC campus map (percentages based on the image)
const ZONE_POSITIONS = {
  "Foot": { x: 28, y: 18 },       // Near the football fields (top-left)
  "Volley": { x: 72, y: 48 },     // Right side of campus
  "Central": { x: 55, y: 78 },    // Bottom center area
  "Hall d'honneur": { x: 50, y: 92 }, // Very bottom
};

const getZoneColor = (zone) => ZONE_COLORS[zone] || { bg: "bg-gray-100", border: "border-gray-400", text: "text-gray-800", dot: "bg-gray-500", hex: "#E5E7EB", borderHex: "#9CA3AF" };

export default function HECGuestPage() {
  const [loading, setLoading] = useState(true);
  const [planning, setPlanning] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeTab, setActiveTab] = useState("carte");
  const [error, setError] = useState(null);
  const [viewService, setViewService] = useState("midi"); // midi or soir
  const [selectedFoodTruck, setSelectedFoodTruck] = useState(null);
  const [showFoodTruckDialog, setShowFoodTruckDialog] = useState(false);
  const [zoom, setZoom] = useState(1);
  const mapRef = useRef(null);

  useEffect(() => {
    loadPublicPlanning();
  }, []);

  const loadPublicPlanning = async () => {
    try {
      // First get list of plannings
      const listRes = await axios.get(`${API_URL}/api/hec/public/plannings`);
      if (listRes.data.length > 0) {
        // Load first planning details
        const detailRes = await axios.get(`${API_URL}/api/hec/public/plannings/${listRes.data[0].id}`);
        setPlanning(detailRes.data);
        if (detailRes.data.planning_days?.length > 0) {
          setSelectedDay(detailRes.data.planning_days[0].date);
        }
      }
    } catch (err) {
      console.error("Error loading planning:", err);
      setError("Impossible de charger les informations de l'événement");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentDaySlots = () => {
    if (!planning || !selectedDay) return { midi: [], soir: [], bars_midi: [], bars_soir: [] };
    return planning.planning_days?.find(d => d.date === selectedDay) || { midi: [], soir: [], bars_midi: [], bars_soir: [] };
  };

  const getFoodTruckInfo = (foodTruckId) => {
    return planning?.food_trucks?.find(ft => ft.id === foodTruckId) || null;
  };

  const groupSlotsByZone = (slots) => {
    const grouped = {};
    slots?.forEach(slot => {
      const zone = slot.zone || "Non assigné";
      if (!grouped[zone]) grouped[zone] = [];
      grouped[zone].push(slot);
    });
    return grouped;
  };

  // Get marker positions from planning or generate default positions
  const getMarkerPositions = () => {
    const slots = viewService === "midi" ? daySlots.midi : daySlots.soir;
    const savedPositions = planning?.map_positions || {};
    const positions = {};
    const zoneCounters = {};

    slots?.forEach((slot, index) => {
      const key = `${selectedDay}-${viewService}-${index}`;
      
      // Use saved position if available
      if (savedPositions[key]) {
        positions[key] = savedPositions[key];
      } else {
        // Generate default position based on zone
        const zone = slot.zone || "Central";
        const zonePos = ZONE_POSITIONS[zone] || { x: 50, y: 50 };
        
        if (!zoneCounters[zone]) zoneCounters[zone] = 0;
        const offset = zoneCounters[zone];
        zoneCounters[zone]++;

        positions[key] = {
          x: Math.min(90, Math.max(10, zonePos.x + (offset % 3) * 4 - 4)),
          y: Math.min(90, Math.max(10, zonePos.y + Math.floor(offset / 3) * 4)),
        };
      }
    });

    return positions;
  };

  const handleMarkerClick = (slot) => {
    const ftInfo = getFoodTruckInfo(slot.food_truck_id);
    setSelectedFoodTruck({ slot, ftInfo });
    setShowFoodTruckDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement de l'événement...</p>
        </div>
      </div>
    );
  }

  if (error || !planning) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center">
        <Card className="p-8 max-w-md">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Aucun événement disponible</h2>
            <p className="text-gray-600">{error || "Aucun événement n'est actuellement programmé."}</p>
          </div>
        </Card>
      </div>
    );
  }

  const daySlots = getCurrentDaySlots();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-amber-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-red-500 rounded-xl flex items-center justify-center">
                <Truck className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">{planning.nom_evenement}</h1>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(parseISO(planning.date_debut), "d", { locale: fr })} - {format(parseISO(planning.date_fin), "d MMMM yyyy", { locale: fr })}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {planning.lieu}
                  </span>
                </div>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-700 border-green-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
              En direct
            </Badge>
          </div>
        </div>
      </header>

      {/* Day selector */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {planning.planning_days?.map(day => (
            <button
              key={day.date}
              onClick={() => setSelectedDay(day.date)}
              className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                selectedDay === day.date 
                  ? "bg-amber-500 text-white shadow-lg" 
                  : "bg-white text-gray-700 hover:bg-amber-100 border border-amber-200"
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              {format(parseISO(day.date), "EEEE d MMMM", { locale: fr })}
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/80 backdrop-blur-sm p-1 rounded-xl shadow-sm border border-amber-200 mb-6">
            <TabsTrigger value="carte" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white rounded-lg px-6">
              <Map className="w-4 h-4 mr-2" />
              Carte
            </TabsTrigger>
            <TabsTrigger value="menus" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white rounded-lg px-6">
              <ChefHat className="w-4 h-4 mr-2" />
              Menus
            </TabsTrigger>
            <TabsTrigger value="bars" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white rounded-lg px-6">
              <Wine className="w-4 h-4 mr-2" />
              Bars
            </TabsTrigger>
          </TabsList>

          {/* CARTE TAB */}
          <TabsContent value="carte" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Map Legend */}
              <div className="lg:col-span-1 space-y-4">
                <Card className="bg-white/90 backdrop-blur-sm border-amber-200">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-amber-600" />
                      Zones & Spécialités
                    </h3>
                    
                    {/* Midi */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Sun className="w-4 h-4 text-amber-500" />
                        <span className="font-medium text-sm">Service du Midi</span>
                      </div>
                      {Object.entries(groupSlotsByZone(daySlots.midi)).map(([zone, slots]) => {
                        const zoneColor = getZoneColor(zone);
                        return (
                          <div key={zone} className={`p-3 rounded-lg mb-2 ${zoneColor.bg} ${zoneColor.border} border`}>
                            <div className="font-medium text-sm mb-1 flex items-center gap-2">
                              <span className={`w-3 h-3 rounded-full ${zoneColor.dot}`}></span>
                              {zone}
                            </div>
                            <div className="space-y-1">
                              {slots.map((slot, idx) => (
                                <div key={idx} className="text-xs text-gray-700 flex items-center gap-2">
                                  <Utensils className="w-3 h-3" />
                                  <span className="font-medium">{slot.food_truck_nom}</span>
                                  <span className="text-gray-500">- {slot.specialite}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Soir */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Moon className="w-4 h-4 text-indigo-500" />
                        <span className="font-medium text-sm">Service du Soir</span>
                      </div>
                      {Object.entries(groupSlotsByZone(daySlots.soir)).map(([zone, slots]) => {
                        const zoneColor = getZoneColor(zone);
                        return (
                          <div key={zone} className={`p-3 rounded-lg mb-2 ${zoneColor.bg} ${zoneColor.border} border`}>
                            <div className="font-medium text-sm mb-1 flex items-center gap-2">
                              <span className={`w-3 h-3 rounded-full ${zoneColor.dot}`}></span>
                              {zone}
                            </div>
                            <div className="space-y-1">
                              {slots.map((slot, idx) => (
                                <div key={idx} className="text-xs text-gray-700 flex items-center gap-2">
                                  <Utensils className="w-3 h-3" />
                                  <span className="font-medium">{slot.food_truck_nom}</span>
                                  <span className="text-gray-500">- {slot.specialite}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Map View */}
              <div className="lg:col-span-2">
                <Card className="bg-white/90 backdrop-blur-sm border-amber-200 overflow-hidden">
                  {/* Map Controls */}
                  <div className="flex items-center justify-between p-3 border-b border-amber-100 bg-white/50">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setViewService("midi")}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                          viewService === "midi" 
                            ? "bg-amber-500 text-white shadow" 
                            : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                        }`}
                      >
                        <Sun className="w-4 h-4" />
                        Midi
                      </button>
                      <button
                        onClick={() => setViewService("soir")}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                          viewService === "soir" 
                            ? "bg-indigo-500 text-white shadow" 
                            : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                        }`}
                      >
                        <Moon className="w-4 h-4" />
                        Soir
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} className="h-8 w-8 p-0">
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-gray-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
                      <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.min(2, z + 0.25))} className="h-8 w-8 p-0">
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <CardContent className="p-0">
                    <div className="relative overflow-auto bg-gray-100" style={{ height: "550px" }}>
                      <div 
                        ref={mapRef}
                        className="relative transition-transform origin-top-left"
                        style={{ 
                          width: `${100 * zoom}%`,
                          minWidth: "100%",
                          transform: `scale(${zoom})`,
                          transformOrigin: "top left"
                        }}
                      >
                        {/* Campus Map Image */}
                        <img 
                          src={MAP_IMAGE_URL}
                          alt="Campus HEC Paris"
                          className="w-full h-auto"
                          draggable={false}
                        />
                        
                        {/* Zone Labels */}
                        <div className="absolute" style={{ top: "12%", left: "25%" }}>
                          <div className="bg-blue-500/90 text-white px-2 py-1 rounded-lg text-xs font-bold shadow-lg backdrop-blur-sm">
                            Zone Foot
                          </div>
                        </div>
                        <div className="absolute" style={{ top: "42%", left: "78%" }}>
                          <div className="bg-yellow-500/90 text-white px-2 py-1 rounded-lg text-xs font-bold shadow-lg backdrop-blur-sm">
                            Zone Volley
                          </div>
                        </div>
                        <div className="absolute" style={{ top: "82%", left: "35%" }}>
                          <div className="bg-emerald-500/90 text-white px-2 py-1 rounded-lg text-xs font-bold shadow-lg backdrop-blur-sm">
                            Zone Central
                          </div>
                        </div>
                        
                        {/* Food Truck Markers */}
                        {(() => {
                          const slots = viewService === "midi" ? daySlots.midi : daySlots.soir;
                          const markerPositions = getMarkerPositions();
                          
                          return slots?.map((slot, index) => {
                            const key = `${selectedDay}-${viewService}-${index}`;
                            const pos = markerPositions[key] || { x: 50, y: 50 };
                            const zoneColor = getZoneColor(slot.zone);
                            
                            return (
                              <div
                                key={key}
                                className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group z-10"
                                style={{ 
                                  left: `${pos.x}%`, 
                                  top: `${pos.y}%`,
                                }}
                                onClick={() => handleMarkerClick(slot)}
                              >
                                {/* Marker */}
                                <div 
                                  className="relative transition-all duration-200 hover:scale-125"
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                  }}
                                >
                                  <div 
                                    className="w-full h-full rounded-full flex items-center justify-center shadow-lg border-3"
                                    style={{ 
                                      backgroundColor: zoneColor.hex,
                                      borderColor: zoneColor.borderHex,
                                      borderWidth: "3px"
                                    }}
                                  >
                                    <Truck className="w-5 h-5" style={{ color: zoneColor.borderHex }} />
                                  </div>
                                  
                                  {/* Pulse animation */}
                                  <div 
                                    className="absolute inset-0 rounded-full animate-ping opacity-30"
                                    style={{ backgroundColor: zoneColor.borderHex }}
                                  />
                                </div>
                                
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-20">
                                  <div className="bg-gray-900/95 text-white text-xs px-3 py-2 rounded-lg shadow-xl whitespace-nowrap backdrop-blur-sm">
                                    <div className="font-bold">{slot.food_truck_nom}</div>
                                    <div className="text-gray-300">{slot.specialite}</div>
                                    <div className="text-amber-400 text-[10px] mt-1">Cliquez pour plus d'infos</div>
                                  </div>
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                    <div className="border-4 border-transparent border-t-gray-900/95"></div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                        
                        {/* Bar Markers (Evening only) */}
                        {viewService === "soir" && daySlots.bars_soir?.map((bar, index) => (
                          <div
                            key={`bar-${index}`}
                            className="absolute transform -translate-x-1/2 -translate-y-1/2 group z-10"
                            style={{ 
                              left: `${50 + index * 8}%`, 
                              top: "88%",
                            }}
                          >
                            <div className="w-9 h-9 bg-purple-500 rounded-full flex items-center justify-center shadow-lg border-3 border-purple-700 hover:scale-110 transition-transform cursor-pointer">
                              <Beer className="w-5 h-5 text-white" />
                            </div>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              <div className="bg-purple-900/95 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                {bar.bar_nom}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Legend */}
                <div className="mt-4 flex flex-wrap gap-3 justify-center">
                  {Object.entries(ZONE_COLORS).map(([zone, colors]) => (
                    <div key={zone} className="flex items-center gap-2 text-sm">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: colors.hex, border: `2px solid ${colors.borderHex}` }}></div>
                      <span className="text-gray-600">{zone}</span>
                    </div>
                  ))}
                  {viewService === "soir" && (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-4 h-4 rounded-full bg-purple-500 border-2 border-purple-700"></div>
                      <span className="text-gray-600">Bars</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* MENUS TAB */}
          <TabsContent value="menus" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {planning.food_trucks?.map(ft => {
                const ftInfo = getFoodTruckInfo(ft.id);
                const menuItems = ftInfo?.menu_items || [];
                return (
                  <Card key={ft.id} className="bg-white/90 backdrop-blur-sm border-amber-200 overflow-hidden hover:shadow-lg transition-shadow">
                    {/* Header with photo */}
                    <div className="h-32 bg-gradient-to-br from-amber-100 to-orange-100 relative">
                      {ft.photo_url || ft.logo_url ? (
                        <img src={ft.photo_url || ft.logo_url} alt={ft.nom} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Truck className="w-12 h-12 text-amber-300" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <h3 className="font-bold text-white text-lg">{ft.nom}</h3>
                        <Badge className="bg-white/20 text-white border-0 text-xs">{ft.specialite}</Badge>
                      </div>
                    </div>
                    
                    <CardContent className="p-4">
                      {menuItems.length > 0 ? (
                        <div className="space-y-3">
                          {menuItems.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-amber-50 transition-colors">
                              {item.photo_url ? (
                                <img src={item.photo_url} alt={item.nom} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                  <Utensils className="w-6 h-6 text-amber-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <span className="font-medium text-gray-800">{item.nom}</span>
                                  <Badge className="bg-amber-100 text-amber-700 border-amber-300 flex-shrink-0">
                                    {item.prix?.toFixed(2) || "0.00"}€
                                  </Badge>
                                </div>
                                {item.description && (
                                  <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                                )}
                                {item.allergenes && item.allergenes.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {item.allergenes.map((a, i) => (
                                      <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                                        {a}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <Utensils className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">Menu à venir</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* BARS TAB */}
          <TabsContent value="bars" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {planning.bars?.map(bar => (
                <Card key={bar.id} className="bg-white/90 backdrop-blur-sm border-amber-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-24 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    {bar.type === "beer_truck" ? (
                      <Beer className="w-12 h-12 text-white/80" />
                    ) : (
                      <Wine className="w-12 h-12 text-white/80" />
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-lg text-gray-800 mb-1">{bar.nom}</h3>
                    <Badge className="mb-3 bg-purple-100 text-purple-700 border-purple-300">
                      {bar.type === "beer_truck" ? "Beer Truck" : "Bar Mobile"}
                    </Badge>
                    
                    {bar.boissons && bar.boissons.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-600 flex items-center gap-2">
                          <GlassWater className="w-4 h-4" />
                          Boissons disponibles
                        </p>
                        <div className="space-y-1">
                          {bar.boissons.map((boisson, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                              <span className="text-sm text-gray-700">{boisson.nom || boisson}</span>
                              {boisson.prix !== undefined && (
                                <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                                  {boisson.prix?.toFixed(2)}€
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-2">
                        Carte des boissons à venir
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Bar schedule */}
            {(daySlots.bars_soir?.length > 0) && (
              <Card className="mt-6 bg-white/90 backdrop-blur-sm border-amber-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    Bars en service ce soir
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {daySlots.bars_soir.map((bar, idx) => (
                      <Badge key={idx} className="bg-purple-100 text-purple-700 border-purple-300 px-3 py-1">
                        <Wine className="w-3 h-3 mr-1" />
                        {bar.bar_nom} - {bar.zone}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Food Truck Detail Dialog */}
      <Dialog open={showFoodTruckDialog} onOpenChange={setShowFoodTruckDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div>{selectedFoodTruck?.slot?.food_truck_nom}</div>
                <Badge className="mt-1 bg-amber-100 text-amber-700">{selectedFoodTruck?.slot?.specialite}</Badge>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedFoodTruck && (
            <div className="space-y-4">
              {/* Location info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">Zone: </span>
                  <Badge variant="outline" className={getZoneColor(selectedFoodTruck.slot.zone).bg}>
                    {selectedFoodTruck.slot.zone}
                  </Badge>
                  {selectedFoodTruck.slot.emplacement && (
                    <span className="text-gray-500">• {selectedFoodTruck.slot.emplacement}</span>
                  )}
                </div>
              </div>

              {/* Menu du jour */}
              {selectedFoodTruck.slot.menu_du_jour && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-1 flex items-center gap-2">
                    <ChefHat className="w-4 h-4" />
                    Menu du jour
                  </h4>
                  <p className="text-sm text-gray-700">{selectedFoodTruck.slot.menu_du_jour}</p>
                </div>
              )}

              {/* Full menu */}
              {selectedFoodTruck.ftInfo?.menu_items?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                    <Utensils className="w-4 h-4" />
                    Carte complète
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedFoodTruck.ftInfo.menu_items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-800">{item.nom}</span>
                          {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                          {item.allergenes?.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {item.allergenes.map((a, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] px-1 py-0 bg-red-50 text-red-600">
                                  {a}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <Badge className="bg-amber-100 text-amber-700 ml-2">
                          {item.prix?.toFixed(2) || "0.00"}€
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No menu yet */}
              {(!selectedFoodTruck.ftInfo?.menu_items || selectedFoodTruck.ftInfo.menu_items.length === 0) && !selectedFoodTruck.slot.menu_du_jour && (
                <div className="text-center py-4 text-gray-500">
                  <Utensils className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Menu à venir</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-amber-200 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>Food Truck United - {planning.client}</p>
        </div>
      </footer>
    </div>
  );
}
